import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Download, AlertCircle } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import Seo from "@/components/Seo";

export default function FreeGuideConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ok | error
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) { setState("error"); return; }
    api.get(`/subscribe/confirm?token=${encodeURIComponent(token)}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [token]);

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="free-guide-confirm-page">
      <Seo title="Confirm Email" path="/free-guide/confirm" />
      {state === "loading" && (
        <div className="flex flex-col items-center gap-4 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p>Confirming your email…</p>
        </div>
      )}

      {state === "ok" && (
        <div data-testid="confirm-success">
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" />
          <h1 className="font-display text-4xl sm:text-5xl font-light">You're confirmed!</h1>
          <p className="text-muted-foreground mt-4 max-w-md mx-auto">
            {data?.available
              ? "Your free guide is ready — download it below (we also emailed you the link)."
              : "Thanks for confirming! We're finishing up the guide and will email your download link very shortly."}
          </p>
          {data?.available && (
            <a href={`${BACKEND_URL}/api/free-guide/download?token=${encodeURIComponent(token)}`}
              data-testid="confirm-download-btn"
              className="inline-flex items-center gap-2 mt-8 bg-primary text-primary-foreground font-mono-label text-xs px-8 py-4 rounded-full hover:brightness-110 transition-[filter]">
              <Download className="w-4 h-4" /> Download your free guide
            </a>
          )}
          <div className="mt-10 inline-block border border-primary/30 bg-primary/5 rounded-xl px-6 py-4">
            <p className="text-sm">
              Welcome gift: use code <span className="font-mono font-semibold text-primary">GROW15</span> for 15% off your first order.
            </p>
            <Link to="/shop" data-testid="confirm-shop-link" className="font-mono-label text-[11px] text-primary hover:underline mt-2 inline-block">
              Start shopping →
            </Link>
          </div>
        </div>
      )}

      {state === "error" && (
        <div data-testid="confirm-error">
          <AlertCircle className="w-14 h-14 text-destructive mx-auto mb-6" />
          <h1 className="font-display text-4xl font-light">Link expired or invalid</h1>
          <p className="text-muted-foreground mt-4">
            This confirmation link didn't work. Please sign up again to get a fresh link.
          </p>
          <Link to="/free-guide" data-testid="confirm-retry-link"
            className="inline-block mt-8 font-mono-label text-xs border border-border rounded-full px-6 py-3 hover:border-primary transition-colors">
            Back to the free guide
          </Link>
        </div>
      )}
    </div>
  );
}
